// Intl.NumberFormat construction is expensive and money is formatted inside
// hot render paths (form-store selectors, list rows); cache per variant.
const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(
  key: string,
  create: () => Intl.NumberFormat,
): Intl.NumberFormat {
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = create();
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function getCurrencyFormatter(
  locale: string,
  currency: string,
  whole: boolean,
): Intl.NumberFormat {
  return getFormatter(
    `${locale}|currency${whole ? "-whole" : ""}|${currency}`,
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        ...(whole
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : {}),
      }),
  );
}

export function getDecimalFormatter(
  locale: string,
  fractionDigits: number,
): Intl.NumberFormat {
  return getFormatter(
    `${locale}|decimal|${fractionDigits}`,
    () =>
      new Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
  );
}

/** A price as the API sends it: an integer `value` at `scaleFactor` decimals. */
export interface MoneyInput {
  value: number;
  scaleFactor: number;
  currency: string;
}

export interface MoneyOptions {
  /** Link colour instead of ink: a price the screen wants noticed. */
  highlight?: boolean;
  /** The price before a discount. The pair draws it struck through beside this one. */
  strikeFrom?: MoneyInput;
  /** Muted text after the amount, already translated ("IVA inclusa"). */
  suffix?: string;
  /** "80 €" / "€80" when the cents are zero, decimals only with cents. Default true. */
  wholeWhenZeroCents?: boolean;
}

export interface MoneyParts {
  /** The whole string exactly as Intl prints it: "80,00 €", "€80.50", "€ 1.200". */
  text: string;
  /** Digits with the locale's grouping: "12.345", "12,345", "1200" (es-ES has none below 10,000). */
  integer: string;
  /** Separator and digits, "" when whole: ",67", ".50". */
  decimals: string;
  /** The number alone, no symbol: integer and decimals, "12.345,67". */
  number: string;
  symbol: string;
  /** Where the locale puts the symbol relative to the number. */
  position: "prefix" | "suffix";
}

export interface MoneyView extends MoneyParts {
  tone: "ink" | "highlight";
  previous?: MoneyParts;
  suffix?: string;
}

const toAmount = ({ value, scaleFactor }: MoneyInput): number =>
  value / 10 ** scaleFactor;

const isWhole = (amount: number, wholeWhenZeroCents: boolean): boolean =>
  wholeWhenZeroCents && Number.isInteger(amount);

// Decimal-mark and symbol are read off format(), never formatToParts: Hermes does not tag
// parts the way V8 does, and format() is what the screen has always printed.
function decimalMark(locale: string): string {
  const sample = getDecimalFormatter(locale, 1).format(1.1);
  const unit = getDecimalFormatter(locale, 0).format(1);
  const mark = sample.slice(sample.indexOf(unit) + unit.length, -unit.length);
  return mark.length > 0 ? mark : ",";
}

const EDGE_SPACE = /^[\s\u00A0\u202F]+|[\s\u00A0\u202F]+$/g;

function symbolOf(locale: string, currency: string): string {
  const withSymbol = getCurrencyFormatter(locale, currency, false).format(1);
  const plain = getDecimalFormatter(locale, 2).format(1);
  return withSymbol.replace(plain, "").replace(EDGE_SPACE, "");
}

function positionOf(locale: string, currency: string): "prefix" | "suffix" {
  const sample = getCurrencyFormatter(locale, currency, false).format(1);
  return sample.search(/\d/) === 0 ? "suffix" : "prefix";
}

function splitNumber(number: string, locale: string, whole: boolean) {
  const cut = whole ? -1 : number.lastIndexOf(decimalMark(locale));
  return {
    number,
    integer: cut === -1 ? number : number.slice(0, cut),
    decimals: cut === -1 ? "" : number.slice(cut),
  };
}

function partsOf(
  price: MoneyInput,
  locale: string,
  wholeWhenZeroCents: boolean,
): MoneyParts {
  const amount = toAmount(price);
  const whole = isWhole(amount, wholeWhenZeroCents);
  try {
    const currencyFormatter = getCurrencyFormatter(
      locale,
      price.currency,
      whole,
    );
    const fractionDigits =
      currencyFormatter.resolvedOptions().maximumFractionDigits ??
      price.scaleFactor;
    const number = getDecimalFormatter(locale, fractionDigits).format(amount);
    return {
      text: currencyFormatter.format(amount),
      ...splitNumber(number, locale, fractionDigits === 0),
      symbol: symbolOf(locale, price.currency),
      position: positionOf(locale, price.currency),
    };
  } catch {
    const fractionDigits = whole ? 0 : price.scaleFactor;
    const number = getDecimalFormatter(locale, fractionDigits).format(amount);
    return {
      text: `${number} ${price.currency}`,
      ...splitNumber(number, locale, fractionDigits === 0),
      symbol: price.currency,
      position: "suffix",
    };
  }
}

/**
 * The one price formatter. The number format follows the locale (it-IT "80 €", en "€80", nl "€ 80",
 * de, es, fr as Intl prints them); whole euros drop their cents and a real fraction keeps them.
 * That holds for every price and charge line (decided 06/10). `wholeWhenZeroCents: false` keeps
 * the cents on a whole amount, for a payout or balance read against a statement.
 */
export function formatMoney(
  price: MoneyInput,
  locale: string,
  options: Pick<MoneyOptions, "wholeWhenZeroCents"> = {},
): string {
  return partsOf(price, locale, options.wholeWhenZeroCents ?? true).text;
}

/** `formatMoney` with the cents kept: a payout, a balance or a fee, where every digit is read against a statement. */
export function formatMoneyExact(price: MoneyInput, locale: string): string {
  return formatMoney(price, locale, { wholeWhenZeroCents: false });
}

/** What a Money component draws: the parts, the tone, the struck old price and the suffix. */
export function formatMoneyParts(
  price: MoneyInput,
  locale: string,
  options: MoneyOptions = {},
): MoneyView {
  const whole = options.wholeWhenZeroCents ?? true;
  const view: MoneyView = {
    ...partsOf(price, locale, whole),
    tone: options.highlight || options.strikeFrom ? "highlight" : "ink",
  };
  if (options.strikeFrom) {
    view.previous = partsOf(options.strikeFrom, locale, whole);
  }
  if (options.suffix !== undefined) view.suffix = options.suffix;
  return view;
}
